// SPDX-License-Identifier: AGPL-3.0-only

import { defineStore } from "pinia";
import {
  loadUserSettings,
  saveUserSettings,
  type DateTimeFormatPreference,
  type DashboardDefaultPreset,
  type UserSettings,
} from "../api/user-settings.js";

export type UserSettingsStatus = "idle" | "loading" | "ready" | "error";

interface UserSettingsState {
  status: UserSettingsStatus;
  settings: UserSettings;
  error: string | null;
}

const DEFAULT_USER_SETTINGS: UserSettings = {
  dateTimeFormat: "iso_local",
  dashboardDefaultPreset: "live",
  dashboardDefaultResolution: "5m",
  updatedAt: null,
};

export const useUserSettingsStore = defineStore("user-settings", {
  state: (): UserSettingsState => ({
    status: "idle",
    settings: DEFAULT_USER_SETTINGS,
    error: null,
  }),
  actions: {
    reset(): void {
      this.status = "idle";
      this.settings = DEFAULT_USER_SETTINGS;
      this.error = null;
    },
    async initialize(force: boolean = false): Promise<void> {
      if (!force && (this.status === "loading" || this.status === "ready")) {
        return;
      }

      this.status = "loading";
      this.error = null;
      try {
        this.settings = await loadUserSettings();
        this.status = "ready";
      } catch (error: unknown) {
        this.status = "error";
        this.error = error instanceof Error ? error.message : "User settings could not be loaded.";
        throw error;
      }
    },
    async updateSettings(dateTimeFormat: DateTimeFormatPreference, dashboardDefaultPreset: DashboardDefaultPreset, dashboardDefaultResolution: string): Promise<UserSettings> {
      this.error = null;
      const settings: UserSettings = await saveUserSettings(dateTimeFormat, dashboardDefaultPreset, dashboardDefaultResolution);
      this.settings = settings;
      this.status = "ready";
      return settings;
    },
  },
});
